import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import SEO from '@/components/SEO';
import { Search, Package, Truck, CheckCircle, Clock, MapPin, FileText } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_STEPS = [
  { key: 'confirmed', label: 'Confirmed', icon: CheckCircle },
  { key: 'processing', label: 'Processing', icon: Package },
  { key: 'shipped', label: 'Shipped', icon: Truck },
  { key: 'delivered', label: 'Delivered', icon: CheckCircle },
];

const FULFILLMENT_MAP = {
  awaiting_payment: { label: 'Awaiting Payment', step: -1 },
  pending: { label: 'Confirmed', step: 0 },
  submitted: { label: 'Processing', step: 1 },
  pending_manual: { label: 'Processing', step: 1 },
  manual: { label: 'Processing', step: 1 },
  printed: { label: 'Printed — Ready to Ship', step: 1 },
  shipped: { label: 'Shipped', step: 2 },
  in_transit: { label: 'In Transit', step: 2 },
  delivered: { label: 'Delivered', step: 3 },
  completed: { label: 'Delivered', step: 3 },
};

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleDateString('en-IN', {
    day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
};

const TrackOrder = () => {
  const [orderNumber, setOrderNumber] = useState('');
  const [email, setEmail] = useState('');
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);

  const handleTrack = async (e) => {
    e.preventDefault();
    if (!orderNumber.trim() || !email.trim()) {
      toast.error('Please enter both order number and email');
      return;
    }
    setLoading(true);
    setSearched(true);
    try {
      const res = await axios.post(`${API}/orders/track`, {
        order_number: orderNumber.trim(),
        email: email.trim(),
      });
      setOrder(res.data);
    } catch (err) {
      setOrder(null);
      toast.error(err.response?.data?.detail || 'Order not found');
    } finally {
      setLoading(false);
    }
  };

  const fulfillment = order ? (FULFILLMENT_MAP[order.fulfillment_status] || FULFILLMENT_MAP[order.order_status] || { label: 'Processing', step: 0 }) : null;
  const currentStep = fulfillment?.step ?? 0;

  return (
    <div className="min-h-screen pt-24 sm:pt-32 pb-16 sm:pb-24" data-testid="track-order-page">
      <SEO title="Track Order - Gangadhar Som" description="Track your artwork order status" noindex />
      <div className="max-w-screen-md mx-auto px-4 sm:px-6">
        <div className="text-center mb-10">
          <h1 className="font-serif text-3xl sm:text-4xl md:text-5xl font-light tracking-tight mb-3" data-testid="track-order-title">
            Track Your Order
          </h1>
          <p className="font-sans text-muted-foreground text-sm sm:text-base">
            Enter your order number and email to check the status of your order.
          </p>
        </div>

        {/* Search Form */}
        <form onSubmit={handleTrack} className="bg-card border border-border rounded-lg p-6 mb-8" data-testid="track-order-form">
          <div className="grid sm:grid-cols-2 gap-4 mb-4">
            <div>
              <Label htmlFor="track-order-number">Order Number</Label>
              <Input
                id="track-order-number"
                data-testid="track-order-number-input"
                value={orderNumber}
                onChange={(e) => setOrderNumber(e.target.value)}
                placeholder="e.g. PRT-A1B2C3D4"
              />
            </div>
            <div>
              <Label htmlFor="track-email">Email Address</Label>
              <Input
                id="track-email"
                type="email"
                data-testid="track-email-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
              />
            </div>
          </div>
          <Button type="submit" disabled={loading} className="w-full bg-accent hover:bg-accent/90" data-testid="track-order-submit">
            <Search size={16} className="mr-2" />
            {loading ? 'Searching...' : 'Track Order'}
          </Button>
        </form>

        {/* Order Result */}
        {searched && !loading && !order && (
          <div className="text-center py-12 bg-card border border-border rounded-lg" data-testid="track-order-not-found">
            <Package size={48} className="mx-auto mb-4 text-muted-foreground" />
            <p className="font-serif text-xl mb-2">Order not found</p>
            <p className="text-sm text-muted-foreground">Please check your order number and email address.</p>
          </div>
        )}

        {order && (
          <div className="space-y-6" data-testid="track-order-result">
            {/* Order Header */}
            <div className="bg-card border border-border rounded-lg p-6">
              <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
                <div>
                  <p className="text-xs uppercase tracking-widest text-muted-foreground font-sans">Order</p>
                  <p className="font-serif text-2xl font-light" data-testid="track-order-number-display">{order.order_number}</p>
                  <p className="text-sm text-muted-foreground font-sans mt-1">Placed {formatDate(order.created_at)}</p>
                </div>
                <div className="text-right">
                  <p className="text-xs uppercase tracking-widest text-muted-foreground font-sans">Status</p>
                  <p className="font-sans text-lg font-medium text-accent" data-testid="track-order-status">
                    {fulfillment?.label || order.order_status}
                  </p>
                </div>
              </div>

              {/* Progress Tracker */}
              <div className="relative" data-testid="track-order-progress">
                <div className="flex justify-between items-center">
                  {STATUS_STEPS.map((step, i) => {
                    const StepIcon = step.icon;
                    const isActive = i <= currentStep;
                    const isCurrent = i === currentStep;
                    return (
                      <div key={step.key} className="flex flex-col items-center flex-1">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center transition-colors ${
                          isActive ? 'bg-accent text-white' : 'bg-muted text-muted-foreground'
                        } ${isCurrent ? 'ring-2 ring-accent ring-offset-2' : ''}`}>
                          <StepIcon size={18} />
                        </div>
                        <span className={`text-xs font-sans mt-2 text-center ${isActive ? 'text-foreground font-medium' : 'text-muted-foreground'}`}>
                          {step.label}
                        </span>
                      </div>
                    );
                  })}
                </div>
                {/* Progress bar */}
                <div className="absolute top-5 left-[12%] right-[12%] h-0.5 bg-muted -z-10">
                  <div
                    className="h-full bg-accent transition-all duration-500"
                    style={{ width: `${Math.max(0, (currentStep / (STATUS_STEPS.length - 1)) * 100)}%` }}
                  />
                </div>
              </div>
            </div>

            {/* Order Details */}
            <div className="bg-card border border-border rounded-lg p-6">
              <h2 className="font-serif text-xl font-light mb-4 flex items-center gap-2">
                <FileText size={18} /> Order Details
              </h2>
              <div className="space-y-3">
                {order.items?.map((item, i) => (
                  <div key={i} className="flex items-center gap-4 py-3 border-b border-border last:border-0">
                    <div className="flex-1">
                      <p className="font-sans text-sm font-medium">{item.title}</p>
                      {item.size_label && (
                        <p className="text-xs text-muted-foreground font-sans">
                          {item.size_label} {item.paper_label ? `/ ${item.paper_label}` : ''} {item.frame_label ? `/ ${item.frame_label}` : ''}
                        </p>
                      )}
                      {item.type === 'digital_download' && (
                        <p className="text-xs text-muted-foreground font-sans">Digital Download</p>
                      )}
                    </div>
                    <p className="font-sans text-sm font-medium">
                      {order.currency === 'INR' ? '₹' : '$'}{(item.unit_price || item.price || 0).toLocaleString()}
                    </p>
                  </div>
                ))}
              </div>
              <div className="mt-4 pt-4 border-t border-border space-y-2">
                <div className="flex justify-between text-sm font-sans">
                  <span className="text-muted-foreground">Subtotal</span>
                  <span>{order.currency === 'INR' ? '₹' : '$'}{(order.subtotal || 0).toLocaleString()}</span>
                </div>
                <div className="flex justify-between text-sm font-sans">
                  <span className="text-muted-foreground">Shipping</span>
                  <span>{order.currency === 'INR' ? '₹' : '$'}{(order.shipping_cost || 0).toLocaleString()}</span>
                </div>
                <div className="flex justify-between font-serif text-lg pt-2 border-t border-border">
                  <span>Total</span>
                  <span className="font-medium text-accent">{order.currency === 'INR' ? '₹' : '$'}{(order.total || 0).toLocaleString()}</span>
                </div>
              </div>
            </div>

            {/* Shipping & Tracking */}
            {order.shipping_address && Object.keys(order.shipping_address).length > 0 && (
              <div className="bg-card border border-border rounded-lg p-6">
                <h2 className="font-serif text-xl font-light mb-4 flex items-center gap-2">
                  <MapPin size={18} /> Shipping Details
                </h2>
                <p className="font-sans text-sm text-foreground">{order.customer_name}</p>
                <p className="font-sans text-sm text-muted-foreground">
                  {[order.shipping_address.address_line1, order.shipping_address.address_line2,
                    order.shipping_address.city, order.shipping_address.state,
                    order.shipping_address.pincode, order.shipping_address.country
                  ].filter(Boolean).join(', ')}
                </p>
                {order.tracking_number && (
                  <div className="mt-4 p-3 bg-muted/50 rounded-lg">
                    <p className="text-xs uppercase tracking-widest text-muted-foreground font-sans mb-1">Tracking Number</p>
                    <p className="font-mono text-sm font-medium" data-testid="track-tracking-number">{order.tracking_number}</p>
                    {order.shipping_carrier && (
                      <p className="text-xs text-muted-foreground mt-1">via {order.shipping_carrier}</p>
                    )}
                  </div>
                )}
                {order.fulfillment_provider && (
                  <p className="text-xs text-muted-foreground mt-3">
                    Fulfilled by: <span className="capitalize">{order.fulfillment_provider}</span>
                  </p>
                )}
              </div>
            )}

            {/* Fulfillment Note */}
            {order.fulfillment_note && (
              <div className="bg-muted/30 border border-border rounded-lg p-4">
                <div className="flex items-center gap-2 mb-1">
                  <Clock size={14} className="text-muted-foreground" />
                  <span className="text-xs uppercase tracking-widest text-muted-foreground font-sans">Latest Update</span>
                </div>
                <p className="font-sans text-sm">{order.fulfillment_note}</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default TrackOrder;
